import { faEye } from "@fortawesome/pro-solid-svg-icons/faEye";
import { faEyeSlash } from "@fortawesome/pro-solid-svg-icons/faEyeSlash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, Button, CardActions, CardContent, Grid, SelectChangeEvent, TextField, Typography } from "@mui/material";
import { AppGlobalClassKeys } from "app/appGlobalStyles";
import { MprcCard, MprcCardActions, MprcFormSubHeader, MprcRequestErrorReport, MprcRequestIndicator, MprcSpinnerIndicator } from "components";
import { ButtonCancel } from "components/buttons";
import { ButtonSend } from "components/buttons/ButtonSend";
import { MprcContainer } from "components/MprcContainer";
import { MprcStringToHtml } from "components/MprcStringToHtml";
import { RecoverPasswordSettingsResponse, StandardAccountResetPasswordStatus } from "generated/CoreApi_v1";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { RootPaths } from "routes/RootRouter";
import { SessionService } from "services/sessionService";
import useCheckPassword from "views/foundation/user/hooks/useCheckPassword";

/////////////////////////////////////////////////////////////////////////////////////////////////

type ResetPasswordRecord = {
    password: string;
    confirmPassword: string;
};

type RouteParams = {
    securityStamp: string;
};

interface IValidationFields {
    password?: string[] | null,
    confirmPassword?: string[] | null,
}

/////////////////////////////////////////////////////////////////////////////////////////////////

export const AccountResetPassword: React.FC = () => {
    const [loading, setLoading] = useState<boolean>(true);
    const [submiting, setSubmiting] = useState<boolean>(false);
    const [showPass, setShowPass] = useState<boolean>(false);
    const [viewError, setViewError] = useState<any>();
    const [lastMessage, setLastMessage] = useState<string>();
    const [settings, setSettings] = useState<RecoverPasswordSettingsResponse>(
        {
            emailSenderAddress: "", emailSenderName: "", username: "",
            passwordAllowNameParts: true, passwordMinLength: 0, passwordRequiresOneNumber: false, passwordRequiresOneSymbol: false, passwordRequiresOneUpperLetter: false
        });
    const { securityStamp } = useParams<RouteParams>();
    const passwordTextRef = useRef<HTMLInputElement | undefined>(undefined);

    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const api = useCoreApi();
    const { t } = useTranslation();

    const [formValidation, setFormValidation] = useState<IValidationFields>({
        password: undefined,
        confirmPassword: undefined,
    });

    const [register, setRegister] = useState<ResetPasswordRecord>({
        password: "",
        confirmPassword: "",
    });

    const { checkPassword } = useCheckPassword(settings, settings?.username);

    const canSave = Object.entries(formValidation).filter(([key, v]) => v !== null).length == 0;

    ///
    function handleUpdateRecord(name: keyof ResetPasswordRecord, v: any) {

        const newState = { ...register, [name]: v };
        let newFormValidation = formValidation!;

        if (formValidation.hasOwnProperty(name)) {
            switch (name) {
                case "password":
                    const np = checkPassword(newState.password);
                    newFormValidation.password = (np.length == 0) ? null : np;
                    break;
                case "confirmPassword":
                    newFormValidation.confirmPassword = (newState.password.length > 0 && newState.confirmPassword.length > 0 && newState.password != newState.confirmPassword)
                        ? [t("Auth.Register.ConfirmPassWrong")]
                        : null;
                    break;
            }
        }

        setRegister(newState);
        setFormValidation(newFormValidation);
    }

    ///
    const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement> | SelectChangeEvent) => {
        const inputName = event.target.name as keyof ResetPasswordRecord;
        handleUpdateRecord(inputName, event.target.value);
    };

    ///
    const handleSubmit = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();

        setSubmiting(true);
        setLastMessage(undefined);

        api.v1.account.standardAccountResetPassword({ securityStamp: securityStamp!, password: register.password })
            .then((response) => {
                var status = response.data.status;

                if (status == StandardAccountResetPasswordStatus.Done) {
                    enqueueSnackbar(t("Auth.ResetPassword.SuccessMessage"), { variant: "success" });
                    navigate(RootPaths.Home, { replace: true });
                }
                else if (status == StandardAccountResetPasswordStatus.Expired) {
                    setLastMessage(t("Auth.ResetPassword.Expired"));
                } else if (status == StandardAccountResetPasswordStatus.NotFound) {
                    setLastMessage(t("Auth.Common.AccountNotFoundMessage"));
                }

                setSubmiting(false);
            })
            .catch((e) => {
                setSubmiting(false);
                setViewError(e);
            });
    };

    const renderError = () => {
        return (
            <Grid container justifyContent="center" alignItems="center" spacing={1}>
                {viewError === 204
                    ? (
                        <Grid size={{ xs: 4 }} sx={{ textAlign: "center" }}>
                            <MprcCard>
                                <CardContent sx={{ minHeight: 100 }}>
                                    <Typography component="div" variant="h3" color="error.main">{t("Auth.AccountResetPassword.InvalidRequest.Title")}</Typography>
                                    <br />
                                    <Typography component="div" variant="h6" color="error.main">{t("Auth.AccountResetPassword.InvalidRequest.Message")}</Typography>
                                </CardContent>
                                <MprcCardActions>
                                    <Button variant="contained" color="muted" onClick={() => navigate("/")}>{t("Auth.AccountResetPassword.InvalidRequest.ButtonReturnText")}</Button>
                                </MprcCardActions>
                            </MprcCard>
                        </Grid>
                    )
                    : (
                        <Grid size={{ xs: 6 }}>
                            <MprcRequestErrorReport error={viewError} />
                        </Grid>
                    )
                }
            </Grid>
        );
    };

    ///
    const passwordForm = () => {
        return (
            <Grid container direction="column" justifyContent="center" alignItems="center" spacing={2} >
                <Grid size="grow">
                    <img
                        src={SessionService.current.appImages.highlightImageUrl}
                        width="360"
                    />
                </Grid>
                <Grid size="grow">
                    <MprcCard sx={{ maxWidth: 450 }} >
                        <form autoComplete="off" spellCheck={false} >

                            <CardContent>

                                {
                                    lastMessage &&
                                    <Alert severity="error" variant="filled" >
                                        {lastMessage}
                                    </Alert>
                                }

                                <MprcContainer>
                                    <MprcFormSubHeader title={t("Auth.AccountResetPassword.Title")} />

                                    <TextField
                                        fullWidth
                                        required
                                        margin="normal"
                                        label={t("Auth.Register.PasswordLabel")}
                                        name={"password"}
                                        type={showPass ? undefined : "password"}
                                        value={register.password}
                                        onChange={(e) => handleChange(e)}
                                        inputRef={passwordTextRef}
                                        slotProps={{
                                            input: {
                                                inputProps: {
                                                    maxLength: 64,
                                                    autoCorrect: "off",
                                                    autocomplete: "new-password1"
                                                },
                                                endAdornment: <FontAwesomeIcon
                                                    className={AppGlobalClassKeys.clickable}
                                                    icon={showPass ? faEye : faEyeSlash}
                                                    fixedWidth
                                                    onClick={() => setShowPass(!showPass)} />
                                            }
                                        }}
                                        error={!!formValidation.password}
                                        helperText={<MprcStringToHtml text={formValidation?.password} />}
                                    />
                                    <TextField
                                        fullWidth
                                        required
                                        margin="normal"
                                        label={t("Auth.Register.PasswordConfirmLabel")}
                                        name={"confirmPassword"}
                                        type={showPass ? undefined : "password"}
                                        value={register.confirmPassword}
                                        onChange={(e) => handleChange(e)}
                                        slotProps={{
                                            input: {
                                                inputProps: {
                                                    maxLength: 64,
                                                    autoCorrect: "off",
                                                    autocomplete: "new-password2"
                                                },
                                                endAdornment: <FontAwesomeIcon
                                                    className={AppGlobalClassKeys.clickable}
                                                    icon={showPass ? faEye : faEyeSlash}
                                                    fixedWidth
                                                    onClick={() => setShowPass(!showPass)}
                                                />
                                            }
                                        }}
                                        error={!!formValidation.confirmPassword}
                                        helperText={<MprcStringToHtml text={formValidation?.confirmPassword} />}
                                    />
                                </MprcContainer>
                            </CardContent>

                            <CardActions sx={{ justifyContent: "center" }}>
                                <ButtonCancel
                                    variant="contained"
                                    disabled={submiting}
                                    onClick={() => navigate(RootPaths.Login, { replace: true })}
                                />

                                <ButtonSend
                                    variant="contained"
                                    type="submit"
                                    color="success"
                                    disabled={!canSave}
                                    onClick={handleSubmit}
                                >
                                    {submiting && <MprcSpinnerIndicator />}
                                </ButtonSend>
                            </CardActions>
                        </form>
                    </MprcCard>
                </Grid>
            </Grid>
        );
    };

    // Ao iniciar pela primeira vez...
    useEffect(() => {
        async function getData() {
            try {
                var result = await api.v1.account.recoverPasswordSettings({ securityStamp: securityStamp! });
                if (result.status == 204) {
                    setViewError(204);
                }
                else {
                    setSettings(result.data);
                }
            }
            catch (e) {
                setViewError(e);
            }
            finally {
                setLoading(false);
            }
        }

        if (loading) getData();
    }, [loading]);

    ///

    if (loading)
        return <MprcRequestIndicator type="waiting" />;

    return (
        viewError
            ? renderError()
            : passwordForm()
    );
};
